import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useServicesStore } from '@/stores/servicesStore';
import { ErrorAlert } from '@nimbitlabs/ui';
import { useTeamsStore } from '@/stores/teamsStore';
import type { Service, ServiceFormData } from '@/types/service';
import { ServiceStatus, ServiceType } from '@/types/api';
import { ArrowLeft, Package } from 'lucide-react';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, TagInput, Textarea } from '@nimbitlabs/ui';

export const EditService: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getServiceById, updateService: updateServiceFn } = useServicesStore();
  const { teams: allTeams } = useTeamsStore();
  
  const [service, setService] = useState<Service | null>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const loadService = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const svc = await getServiceById(id);
        setService(svc as Service);
      } catch (e) {
        console.error('Failed to load service:', e);
      } finally {
        setLoading(false);
      }
    };
    loadService();
  }, [id, getServiceById]);
  
  const [error, setError] = useState<unknown>(null);
  
  const [formData, setFormData] = useState<ServiceFormData>({
    name: '',
    description: '',
    version: '1.0.0',
    status: ServiceStatus.Healthy,
    type: ServiceType.Api,
    teamId: '',
    tags: []
  });

  useEffect(() => {
    if (service) {
      setFormData({
        name: service.name,
        description: service.description,
        version: service.version || '1.0.0',
        status: service.status,
        type: service.type,
        teamId: service.teamId,
        tags: service.tags || []
      });
    }
  }, [service]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!service) return;

    try {
      await updateServiceFn(service.id, {
        id: service.id,
        data: formData
      });
      navigate(`/services/${id}`);
    } catch (err) {
      console.error('Failed to update service:', err);
      setError(err);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild>
            <Link to="/services">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Services
            </Link>
          </Button>
        </div>
        <Card>
          <CardContent className="text-center py-12">
            <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4 animate-pulse" />
            <h3 className="text-lg font-medium mb-2">Loading service...</h3>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild>
            <Link to="/services">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Services
            </Link>
          </Button>
        </div>
        <Card>
          <CardContent className="text-center py-12">
            <h3 className="text-lg font-medium mb-2">Service not found</h3>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" asChild>
          <Link to={`/services/${id}`}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Service
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Edit Service</CardTitle>
          <CardDescription>
            Update service information in your catalog.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Error Display */}
          <ErrorAlert error={error} className="mb-4" />
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Service Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="My Service"
                  required
                  maxLength={200}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Type *</Label>
                <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value as ServiceFormData['type'] })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ServiceType.Api}>API</SelectItem>
                    <SelectItem value={ServiceType.Microservice}>Microservice</SelectItem>
                    <SelectItem value={ServiceType.Database}>Database</SelectItem>
                    <SelectItem value={ServiceType.Frontend}>Frontend</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe what this service does..."
                required
                maxLength={1000}
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="team">Team *</Label>
              <Select value={formData.teamId} onValueChange={(value) => setFormData({ ...formData, teamId: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a team" />
                </SelectTrigger>
                <SelectContent>
                  {allTeams.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Tags</Label>
              <TagInput
                tags={formData.tags}
                onTagsChange={(tags) => setFormData({ ...formData, tags })}
                placeholder="Add tags (e.g. frontend, api, production)"
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => navigate(`/services/${id}`)}>
                Cancel
              </Button>
              <Button type="submit">
                Update Service
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
